/* ==========================================================================
   Verona Tempo — homepage sections (round 10 B). Loaded with "defer" by the sections that need it
   (the browser downloads it once; the guard below makes it run once).
   1. Sideways rows with previous / next buttons   [data-vt-scroller="id"] + [data-vt-scroller-nav="id"]
      (Shop by category, Best sellers, Last pairs). Buttons show only when the row is wider than the screen;
      swipe / trackpad / keyboard scrolling always work. Right-to-left aware.
   2. New arrivals tabs                             [data-vt-newin] — tab = that collection's newest in-stock
      products, fetched once with the section rendering API (?section_id=vt-home-new-in&sort_by=created-descending).
   3. Instagram / TikTok video tabs                 [data-vt-social-tabs] — nothing from TikTok / Instagram loads
      until she opens a tab (the embed waits in a <template>; the embed script is added on that click).
   4. "Picked for this season" background video     [data-vt-season-video] — only when motion is allowed and the
      phone is not saving data; pause / play button; stops while off screen.
   Theme editor: everything re-initialises on shopify:section:load.
   r11 (B): 1–3 set themselves up only when she scrolls near them (whenNear); the season video already waits.
   ========================================================================== */
(function () {
  'use strict';
  if (window.vtHome) return;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  function prefersReduce() { return !!(reduce && reduce.matches); }
  function isRtl() { return document.documentElement.getAttribute('dir') === 'rtl'; }
  function root() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.charAt(r.length - 1) === '/' ? r : r + '/';
  }

  /* ---------- 1. sideways rows ---------- */
  function initScroller(track) {
    if (track.__vtScroller) return;
    track.__vtScroller = true;
    var id = track.getAttribute('data-vt-scroller');
    var nav = document.querySelector('[data-vt-scroller-nav="' + id + '"]');
    if (!nav) return;
    var prev = nav.querySelector('[data-vt-scroll="prev"]');
    var next = nav.querySelector('[data-vt-scroll="next"]');
    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var pos = Math.abs(track.scrollLeft);
      nav.hidden = max <= 4;
      if (prev) prev.disabled = pos <= 2;
      if (next) next.disabled = pos >= max - 2;
    }
    function go(dir) {
      var step = Math.max(track.clientWidth * 0.85, 200) * dir * (isRtl() ? -1 : 1);
      track.scrollBy({ left: step, behavior: prefersReduce() ? 'auto' : 'smooth' });
    }
    if (prev) prev.addEventListener('click', function () { go(-1); });
    if (next) next.addEventListener('click', function () { go(1); });
    var raf = 0;
    track.addEventListener('scroll', function () {
      if (raf) return;
      raf = window.requestAnimationFrame(function () { raf = 0; update(); });
    }, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    else window.addEventListener('resize', update);
    update();
  }

  /* ---------- 2. new arrivals tabs ---------- */
  function initNewIn(sec) {
    if (sec.__vtNewIn) return;
    sec.__vtNewIn = true;
    var list = sec.querySelector('[data-vt-ni-tablist]');
    var panel = sec.querySelector('[data-vt-ni-panel]');
    var grid = sec.querySelector('[data-vt-ni-grid]');
    var status = sec.querySelector('[data-vt-ni-status]');
    var allLink = sec.querySelector('[data-vt-ni-all]');
    if (!list || !panel || !grid) return;
    var tabs = [].slice.call(list.querySelectorAll('[role="tab"]'));
    var cache = { all: grid.innerHTML };
    var busy = null;
    var file = sec.getAttribute('data-section-file') || 'vt-home-new-in';

    function select(tab, focus) {
      var key = tab.getAttribute('data-vt-ni-tab');
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
      });
      if (focus) tab.focus();
      panel.setAttribute('aria-labelledby', tab.id);
      var label = tab.getAttribute('data-label') || tab.textContent.trim();
      if (allLink) {
        allLink.href = tab.getAttribute('data-url') || allLink.href;
        allLink.textContent = key === 'all' ? allLink.getAttribute('data-label-all') : (allLink.getAttribute('data-label-tpl') || '').replace('[label]', label);
      }
      if (cache[key] != null) { show(cache[key], label); return; }
      if (busy) busy.abort && busy.abort();
      panel.setAttribute('aria-busy', 'true');
      sec.classList.add('is-loading');
      if (status) status.textContent = sec.getAttribute('data-loading') || '';
      var ctrl = window.AbortController ? new AbortController() : null;
      busy = ctrl;
      var url = tab.getAttribute('data-url') + (tab.getAttribute('data-url').indexOf('?') > -1 ? '&' : '?') + 'sort_by=' + encodeURIComponent(tab.getAttribute('data-sort') || 'created-descending') + '&section_id=' + encodeURIComponent(file);
      fetch(url, { credentials: 'same-origin', signal: ctrl ? ctrl.signal : undefined })
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var frag = doc.querySelector('[data-vt-ni-fragment]');
          if (!frag) throw new Error('no fragment');
          cache[key] = frag.innerHTML;
          // only draw it when this tab is still the chosen one
          if (tab.getAttribute('aria-selected') === 'true') show(cache[key], label);
        })
        .catch(function (e) {
          if (e && e.name === 'AbortError') return;
          if (tab.getAttribute('aria-selected') === 'true') {
            grid.innerHTML = '<p class="vt-ni__error">' + (sec.getAttribute('data-error') || '') + '</p>';
            if (status) status.textContent = sec.getAttribute('data-error') || '';
          }
        })
        .then(function () {
          if (busy === ctrl) busy = null;
          panel.removeAttribute('aria-busy');
          sec.classList.remove('is-loading');
        });
    }
    function show(html, label) {
      grid.innerHTML = html;
      panel.removeAttribute('aria-busy');
      sec.classList.remove('is-loading');
      if (status) status.textContent = (sec.getAttribute('data-shown') || '').replace('[label]', label);
      document.dispatchEvent(new CustomEvent('vt:content-added', { detail: { root: grid } }));
    }
    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { if (t.getAttribute('aria-selected') !== 'true') select(t, false); });
      t.addEventListener('keydown', function (e) {
        var j = null;
        var fwd = isRtl() ? 'ArrowLeft' : 'ArrowRight';
        var back = isRtl() ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === fwd) j = (i + 1) % tabs.length;
        else if (e.key === back) j = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = tabs.length - 1;
        if (j === null) return;
        e.preventDefault();
        select(tabs[j], true);
      });
    });
  }

  /* ---------- 3. TikTok / Instagram tabs ---------- */
  var loadedScripts = {};
  function addScript(src, done) {
    if (loadedScripts[src]) { if (done) loadedScripts[src].then(done); return; }
    loadedScripts[src] = new Promise(function (res) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = res;
      s.onerror = res;
      document.body.appendChild(s);
    });
    if (done) loadedScripts[src].then(done);
  }
  function loadPanel(panel) {
    if (!panel || panel.__vtLoaded) return;
    var tpl = panel.querySelector('template[data-vt-social-embed]');
    var stage = panel.querySelector('[data-vt-social-stage]');
    if (!tpl || !stage) return;
    panel.__vtLoaded = true;
    stage.innerHTML = '';
    stage.appendChild(tpl.content.cloneNode(true));
    stage.classList.add('is-embedded');
    var src = tpl.getAttribute('data-script');
    if (src.indexOf('instagram.com') > -1) {
      if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process();
      else addScript(src, function () { if (window.instgrm && window.instgrm.Embeds) window.instgrm.Embeds.process(); });
    } else {
      // TikTok's script scans the page for .tiktok-embed when it runs; a second run re-scans
      if (loadedScripts[src]) { delete loadedScripts[src]; }
      addScript(src);
    }
  }
  function initSocial(sec) {
    if (sec.__vtSocial) return;
    sec.__vtSocial = true;
    var tabs = [].slice.call(sec.querySelectorAll('[role="tab"]'));
    function panelFor(t) { return document.getElementById(t.getAttribute('aria-controls')); }
    function select(tab, focus, load) {
      tabs.forEach(function (t) {
        var on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        var p = panelFor(t);
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
      if (load) loadPanel(panelFor(tab));
    }
    tabs.forEach(function (t, i) {
      // opening a tab (a click / tap / Enter, even on the tab already shown) loads its videos
      t.addEventListener('click', function () { select(t, false, true); });
      t.addEventListener('keydown', function (e) {
        var j = null;
        var fwd = isRtl() ? 'ArrowLeft' : 'ArrowRight';
        var back = isRtl() ? 'ArrowRight' : 'ArrowLeft';
        if (e.key === fwd) j = (i + 1) % tabs.length;
        else if (e.key === back) j = (i - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') j = 0;
        else if (e.key === 'End') j = tabs.length - 1;
        if (j === null) return;
        e.preventDefault();
        select(tabs[j], true, false); // arrow keys only move; Enter / Space (click) loads
      });
    });
    [].slice.call(sec.querySelectorAll('[data-vt-social-load]')).forEach(function (b) {
      b.addEventListener('click', function () { loadPanel(b.closest('[data-vt-social-panel]')); });
    });
  }

  /* ---------- 4. season background video ---------- */
  function initSeason(video) {
    if (video.__vtSeason) return;
    video.__vtSeason = true;
    var saveData = navigator.connection && navigator.connection.saveData;
    if (prefersReduce() || saveData) return; // photo only
    // 9 Oct 2026 (owner: "pictures are not loading fast on the phone, remove videos"): phones keep the photo — the
    // phone video was a 2.6 MB full-HD file. Computers still get the video.
    if (window.innerWidth < 768) return;
    var sec = video.closest('.vt-season');
    var btn = sec && sec.querySelector('[data-vt-season-pause]');
    var userPaused = false;
    function pick() {
      // phones take the portrait video when the section has one (data-msrc), computers the wide one (data-src)
      var attr = window.innerWidth < 768 && video.querySelector('source[data-msrc]') ? 'data-msrc' : 'data-src';
      var sources = [].slice.call(video.querySelectorAll('source[' + attr + ']'));
      if (!sources.length) return false;
      var want = window.innerWidth * Math.min(window.devicePixelRatio || 1, 2);
      sources.sort(function (a, b) { return (+a.getAttribute('data-width') || 0) - (+b.getAttribute('data-width') || 0); });
      var chosen = sources[sources.length - 1];
      for (var i = 0; i < sources.length; i++) {
        if ((+sources[i].getAttribute('data-width') || 0) >= Math.min(want, 1920)) { chosen = sources[i]; break; }
      }
      video.src = chosen.getAttribute(attr);
      return true;
    }
    function play() {
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }
    function start() {
      if (!video.getAttribute('src') && !pick()) return;
      video.addEventListener('playing', function () {
        sec.classList.add('is-playing');
        if (btn) btn.hidden = false;
      }, { once: true });
      play();
    }
    if (btn) {
      btn.addEventListener('click', function () {
        var paused = btn.getAttribute('aria-pressed') === 'true';
        if (paused) { userPaused = false; play(); } else { userPaused = true; video.pause(); }
        btn.setAttribute('aria-pressed', paused ? 'false' : 'true');
        btn.setAttribute('aria-label', btn.getAttribute(paused ? 'data-label-pause' : 'data-label-play'));
      });
    }
    if ('IntersectionObserver' in window) {
      var started = false;
      new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (en.isIntersecting) {
            if (!started) { started = true; start(); } else if (!userPaused) play();
          } else if (started) video.pause();
        });
      }, { rootMargin: '200px 0px' }).observe(sec || video);
    } else start();
  }

  /* ---------- r11 (B): lighter start — rows further down the page set themselves up only when she gets near them
     (600 px before they come into view). The HTML (products, links, texts) is in the page from the start, so nothing
     is hidden from search engines and nothing looks different; only the small scripts wait. ---------- */
  var lazyIO = null;
  function whenNear(el, fn) {
    if (!('IntersectionObserver' in window)) { fn(el); return; }
    var r = el.getBoundingClientRect();
    if (r.top < window.innerHeight + 600 && r.bottom > -600) { fn(el); return; } // already near: at once
    if (el.__vtWait) return;
    el.__vtWait = fn;
    if (!lazyIO) {
      lazyIO = new IntersectionObserver(function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          lazyIO.unobserve(en.target);
          var f = en.target.__vtWait;
          en.target.__vtWait = null;
          if (f) f(en.target);
        });
      }, { rootMargin: '600px 0px' });
    }
    lazyIO.observe(el);
  }


  /* Last pairs photos are lined up by assets/vt-line.js, like every product photo on the site. */

  function init(scope) {
    scope = scope || document;
    [].slice.call(scope.querySelectorAll('[data-vt-scroller]')).forEach(function (el) { whenNear(el, initScroller); });
    [].slice.call(scope.querySelectorAll('[data-vt-newin]')).forEach(function (el) { whenNear(el, initNewIn); });
    [].slice.call(scope.querySelectorAll('[data-vt-social-tabs]')).forEach(function (el) { whenNear(el, initSocial); });
    [].slice.call(scope.querySelectorAll('[data-vt-season-video]')).forEach(initSeason);
  }
  window.vtHome = { init: init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
